import { useEffect, useMemo, useState } from "react";
import { Calculator, X } from "lucide-react";

/**
 * MortgageCalculatorModal
 *
 * Iconic entry point + modal, matches the iKEY dark-glass aesthetic. Used on
 * both the agent MLS Search page and the client portal. Pre-fills sales
 * price when opened from a specific property (via `initialPrice`).
 *
 * Standard amortization formula:
 *     M = P * r(1+r)^n / ((1+r)^n - 1)
 * where
 *     P = principal (sales_price - down_payment)
 *     r = monthly rate = (interest_rate / 12)
 *     n = months = term_years * 12
 * Returns null when P <= 0 or n <= 0 so the UI can show "—".
 */
function calcMonthly(principal, annualRate, years) {
  const n = Math.round(years * 12);
  if (!(principal > 0) || !(n > 0)) return null;
  if (!(annualRate > 0)) return principal / n;
  const r = (annualRate / 100) / 12;
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

const fmt = (n) => (n == null || !Number.isFinite(n))
  ? "—"
  : n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export function MortgageCalculatorButton({ initialPrice = 0, className = "", size = "md" }) {
  const [open, setOpen] = useState(false);
  // md matches the `text-3xl` MLS-Search header lockup; sm keeps the older
  // inline-in-nav footprint for any callers that need a compact chip.
  const box = size === "sm" ? "h-9 w-9" : "h-11 w-11 sm:h-12 sm:w-12";
  const glyph = size === "sm" ? "h-4 w-4" : "h-6 w-6 sm:h-7 sm:w-7";
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex ${box} items-center justify-center rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors shrink-0 ${className}`.trim()}
        aria-label="Open mortgage calculator"
        title="Mortgage calculator"
        data-testid="mortgage-calc-open"
      >
        <Calculator className={glyph} strokeWidth={1.75} />
      </button>
      {open && <MortgageCalculatorModal initialPrice={initialPrice} onClose={() => setOpen(false)} />}
    </>
  );
}

function MortgageCalculatorModal({ initialPrice, onClose }) {
  const [price, setPrice] = useState(initialPrice > 0 ? initialPrice : 750_000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(30);

  // Sync the dollar / percentage down-payment inputs so editing either
  // updates the other consistently without a feedback loop.
  const downDollars = useMemo(() => Math.round((price || 0) * (downPct / 100)), [price, downPct]);
  const setDownDollars = (d) => setDownPct(price > 0 ? Math.max(0, Math.min(100, (d / price) * 100)) : 0);

  const principal = Math.max(0, (price || 0) - downDollars);
  const monthly = useMemo(() => calcMonthly(principal, rate, years), [principal, rate, years]);
  const totalInterest = monthly != null && Number.isFinite(monthly)
    ? Math.max(0, monthly * years * 12 - principal)
    : null;

  // Escape closes; body scroll lock; focus trap is skipped for scope.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const inputCls = "w-full rounded-md border border-white/20 bg-white/10 backdrop-blur px-3 py-2 text-sm text-white placeholder:text-white/40 focus:bg-white/15 focus:border-white/50 focus:outline-none transition-colors tabular-nums";
  const labelCls = "block text-[11px] font-semibold uppercase tracking-widest text-white/70";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-slate-950/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="mortgage-calc-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-slate-950/85 backdrop-blur-xl ring-1 ring-white/15 shadow-2xl text-white overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div className="flex items-center gap-2.5">
            <Calculator className="h-5 w-5 text-white/80" strokeWidth={1.75} />
            <h2 className="font-heading text-xl font-bold tracking-tight">Mortgage calculator</h2>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-md hover:bg-white/10 flex items-center justify-center"
            aria-label="Close"
            data-testid="mortgage-calc-close"
          >
            <X className="h-4 w-4 text-white/70" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <label className="block">
            <span className={labelCls}>Sales price</span>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 text-sm">$</span>
              <input
                type="number" min="0" step="1000" value={price}
                onChange={(e) => setPrice(Number(e.target.value) || 0)}
                className={`${inputCls} pl-7`}
                data-testid="mortgage-price"
              />
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={labelCls}>Down payment %</span>
              <div className="relative mt-1.5">
                <input
                  type="number" min="0" max="100" step="0.5" value={downPct}
                  onChange={(e) => setDownPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className={`${inputCls} pr-7`}
                  data-testid="mortgage-down-pct"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 text-sm">%</span>
              </div>
            </label>
            <label className="block">
              <span className={labelCls}>Down payment $</span>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 text-sm">$</span>
                <input
                  type="number" min="0" step="1000" value={downDollars}
                  onChange={(e) => setDownDollars(Number(e.target.value) || 0)}
                  className={`${inputCls} pl-7`}
                  data-testid="mortgage-down-dollars"
                />
              </div>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={labelCls}>Interest rate</span>
              <div className="relative mt-1.5">
                <input
                  type="number" min="0" max="25" step="0.05" value={rate}
                  onChange={(e) => setRate(Number(e.target.value) || 0)}
                  className={`${inputCls} pr-7`}
                  data-testid="mortgage-rate"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 text-sm">%</span>
              </div>
            </label>
            <label className="block">
              <span className={labelCls}>Loan term</span>
              <select
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className={`${inputCls} mt-1.5 appearance-none pr-3`}
                data-testid="mortgage-years"
              >
                {[10, 15, 20, 25, 30].map(y => (
                  <option key={y} value={y} className="text-slate-900">{y}-year fixed</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="px-6 pb-6 pt-4 border-t border-white/10 bg-white/5">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-white/60">Estimated monthly payment</div>
          <div className="mt-1 font-heading text-4xl sm:text-5xl font-bold tracking-tight tabular-nums drop-shadow" data-testid="mortgage-monthly">
            {fmt(monthly)}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-white/70">
            <div>
              <div className="uppercase tracking-widest text-[10px] text-white/50">Loan amount</div>
              <div className="mt-0.5 font-semibold text-white tabular-nums">{fmt(principal)}</div>
            </div>
            <div>
              <div className="uppercase tracking-widest text-[10px] text-white/50">Total interest</div>
              <div className="mt-0.5 font-semibold text-white tabular-nums">{fmt(totalInterest)}</div>
            </div>
          </div>
          <div className="mt-4 text-[10px] leading-relaxed text-white/50">
            Principal &amp; interest only. Excludes taxes, HOA, and insurance. Estimate — confirm with your lender.
          </div>
        </div>
      </div>
    </div>
  );
}
